export const LANGUAGE_STORAGE_KEY = 'tmc_language'

export const STATE = {
  NULL: 'NULL',
  STARTED: 'STARTED',
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
  FINISHED: 'FINISHED',
  READY: 'READY',
  ERROR: 'ERROR',
  LOADING: 'LOADING',
  INSURED: 'INSURED',
  ACCEPTED_NO_BSN: 'ACCEPT_NO_BSN',
  DUPLICATE_BSN: 'DUPLICATE_BSN',
  PROCESSING: 'PROCESSING',
  REVIEW: 'REVIEW',
  // insurance_state, backend shared/models/insurance.py. UNINSURED en DECEASED
  // zijn een uitspraak over de patient, NODATA en FAILED alleen over de check.
  UNINSURED: 'UNINSURED',
  DECEASED: 'DECEASED',
  NODATA: 'NODATA',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
} as const

export const CONSULT_STATE = {
  OPEN: 0,
  FINALIZED: 1,
  LOCKED: 2,
  CANCELLED: 3,
  NO_SHOW: 4,
} as const

export const RESPONSE = {
  RESP_SUCCESS: 200, // OK, everything is as expected.
  RESP_INPUT_MISSING: 400, // One of the required inputs is missing
  RESP_UNAUTHORIZED: 401, // Authorisation failed by login (username and password) or by token
  RESP_PAGE_NOT_FOUND: 404, // Endpoint does not exist.
  RESP_CONFLICTING_DATA: 409, // Record can not be inserted/updated because it would conflict with other records.
  RESP_PRECONDITION_FAILED: 412, // Not all requirements for action are met.
  RESP_CONTENT_TOO_LARGE: 413, // Request exceeded what we are willing to handle.
  RESP_INPUT_VALIDATION_ERROR: 417, // One of the inputs did not validate (JSON will tell which) / DB constraints
  RESP_BLOCKED_IP_RATE_LIMIT: 429, // Client is (temporarily) blocked because IP rate limit is reached
  RESP_FAILED_DEPENDENCY: 430, // Call to Payment provider failed
  RESP_TOKEN_EXPIRED: 498, // Token has expired

  // specific responses
  RESP_IN_PROGRESS: 489, // Process already started, please wait.
  RESP_MANUAL_REVIEW: 490, // Case needs a manual review
  RESP_INPUT_LOGIC_ERROR: 491, // A logic/conditional error has occurred
  RESP_EXISTING_DATA: 492, // Record can not be inserted because it already exists.
  RESP_BLOCKED_USER_RATE_LIMIT: 494, // Client is (temporarily) blocked because a user identifiable rate limit is reached
  RESP_RECORD_NOT_FOUND: 495, //
  RESP_CONCURRENCY_ERROR: 488, // Trying to overwrite record with stale data

  // two-factor responses
  RESP_2F_CODE_TOOMANYTRIES: 496, // Too many two-factor code (e-mail or SMS) tries
  RESP_2F_CODE_WRONG: 493, // Wrong two-factor code (e-mail or SMS)
  RESP_2F_CODE_EXPIRED: 499, // Two-factor code (e-mail or SMS) has expired

  // registration specific responses
  RESP_REG_EMAIL_ALREADY_REGISTERED: 497, // A user with this e-mail address is already registered

  //
  RESP_BACKEND_ERROR: 500, // Any technical error in the back-end (DB down, etc)
} as const

export const PCE = {
  PATIENT_TOO_YOUNG: 'PCE_PATIENT_TOO_YOUNG',
  ADD_CONSULT_FUTURE_ALREADY_EXISTS: 'PCE_ADD_CONSULT_FUTURE_ALREADY_EXISTS',
  ADD_CONSULT_PAST_STILL_OPEN: 'PCE_ADD_CONSULT_PAST_STILL_OPEN',
  PATIENT_NOT_ONBOARDED: 'PCE_PATIENT_NOT_ONBOARDED',
  PATIENT_DISABLED: 'PCE_PATIENT_DISABLED',
  REVIEW_EXPIRED: 'PCE_REVIEW_EXPIRED',
  REVIEW_CONSULT_DROPPED: 'PCE_REVIEW_CONSULT_DROPPED',
  REVIEW_CONSULT_NOT_ENDED: 'PCE_REVIEW_CONSULT_NOT_ENDED',
  DEL_CONSULT_TOO_SOON: 'PCE_DEL_CONSULT_TOO_SOON',
  DEL_CONSULT_NOT_ALLOWED: 'PCE_DEL_CONSULT_NOT_ALLOWED',
  MV_CONSULT_NOT_POSSIBLE: 'PCE_MV_CONSULT_NOT_POSSIBLE',
  DIARY_INVALID_CATEGORY: 'PCE_DIARY_INVALID_CATEGORY',
  DIARY_INVALID_DATE_FORMAT: 'PCE_DIARY_INVALID_DATE_FORMAT',
  DIARY_DATE_IN_FUTURE: 'PCE_DIARY_DATE_IN_FUTURE',
  DIARY_DATE_TOO_OLD: 'PCE_DIARY_DATE_TOO_OLD',
  DIARY_NOTES_TOO_LONG: 'PCE_DIARY_NOTES_TOO_LONG',
  DIARY_INVALID_NOTES: 'PCE_DIARY_INVALID_NOTES',
  DIARY_INVALID_IMPACT: 'PCE_DIARY_INVALID_IMPACT',
  DIARY_IMPACT_REQUIRED: 'PCE_DIARY_IMPACT_REQUIRED',
  DIARY_IMPACT_NOT_ALLOWED: 'PCE_DIARY_IMPACT_NOT_ALLOWED',
  DIARY_INVALID_MEDICATION: 'PCE_DIARY_INVALID_MEDICATION',
  DIARY_TRIPTAN_NOT_MIGRAINE: 'PCE_DIARY_TRIPTAN_NOT_MIGRAINE',
  DIARY_PAINKILLER_NOT_ALLOWED: 'PCE_DIARY_PAINKILLER_NOT_ALLOWED',
  MESSAGE_TOO_LONG: 'PCE_MESSAGE_TOO_LONG',
} as const

export const ANSWER_YN = {
  YES: '1',
  NO: '0',
} as const

export const CONSULT_CAT = {
  INTAKE: 0,
  HH: 1,
  BOTOX: 2,
  GONBLOCK: 3,
  UNPLANNED: 4,
} as const

// modes
export const CONSULT_MODE = {
  POLI: 0,
  PHONE: 1,
  VIDEO: 2,
  UNKNOWN: 3,
} as const

// types
export const CONSULT_TYPE = {
  IC_POLI: 1,
  IC_BB: 2,
  HC_POLI: 3,
  HC_BB: 4,
  HC_TEL: 5,
  HC_UNPLANNED: 6,
  BOTOX: 7,
  GONBLOCK: 8,
  PRESCRIPTION: 9,
} as const

export const DEPARTMENT = {
  OFFICE: 'office',
  MEDICAL: 'medical',
  FINANCE: 'finance',
} as const

export const APPOINTMENT_MAX_DAYS_FROM_NOW: number = 180

export const MAX_MESSAGE_LENGTH: number = 2000

// Allowed modes per plannable category — mirrors backend PLANNABLE_CONSULT_CAT_MODES
export const PLANNABLE_CAT_MODES: Record<number, number[]> = {
  [CONSULT_CAT.INTAKE]:   [CONSULT_MODE.POLI, CONSULT_MODE.VIDEO],
  [CONSULT_CAT.HH]:       [CONSULT_MODE.POLI, CONSULT_MODE.PHONE, CONSULT_MODE.VIDEO],
  [CONSULT_CAT.BOTOX]:    [CONSULT_MODE.POLI],
  [CONSULT_CAT.GONBLOCK]: [CONSULT_MODE.POLI],
}

export const CATEGORY = {
  PRESCRIPTION: 'PRESCRIPT',
  PRESCRIPTION_CGRP_STAT: 'PRESCRIPT_CGRP_STAT',
  GP_LETTER: 'GP_LETTER',
  MRI_RESULT: 'MRI_RESULT',
  CLINIC_MAIL_ATTACHMENT: 'CLINIC_MAIL_ATTACHMENT',
  INT_MAIL_ATTACHMENT: 'INT_MAIL_ATTACHMENT',
} as const

export const ADDON_BOTOX: string = '17115787'

export const REBATE_STATE = {
  OPEN: 'REQUESTED',
  APPROVED: 'APPROVED',
  DENIED: 'DENIED',
} as const

export const QUESTIONNAIRE_TYPE_MED = {
  INTAKE_MIGRAINE: 'IN_MIGR',
} as const
